import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { toast } from "sonner";

import { useHookCache } from "../../lib/hookCache";
import {
  OVERRIDE_SUPERSEDED,
  useAnalyticsResultOverride,
} from "./useAnalyticsResultOverride";
import { type AnalyticsColumnResult } from "../../models/audit/analyticsColumnResult";
import { analyticsSummaryToCheckResult } from "../../models/audit/analyticsTranslator";
import {
  type AuditSession,
  type ProcedureColumn,
} from "../../models/audit/auditSession";
import { type OutcomeType } from "../../models/audit/attributeTestColumn";

/** The selection an override's successor has to be followed onto. */
export interface AnalyticsOverrideRetarget {
  sourceRowId?: string;
  checkResultId: string;
  evalColumnId?: string;
}

interface UseAnalyticsOverrideFlowProps {
  auditSession: AuditSession;
  /** The verdict being concluded on, and the cell selection that named it. */
  analyticsResultId: string | undefined;
  analyticsResult: AnalyticsColumnResult | undefined;
  column: ProcedureColumn | undefined;
  checkResultId?: string;
  sourceRowId?: string;
  /** Re-read of the host's own verdict detail, under the selection's key. */
  refetch: () => Promise<void>;
  /**
   * How this host moves its cell onto the successor verdict. The two hosts
   * retarget through different wiring — the sheet is handed a selection
   * callback by the work-paper, the fold-out drives the audit context's own
   * selection — so the act is theirs and only the target is computed here.
   */
  onRetarget: (selection: AnalyticsOverrideRetarget) => void;
  /**
   * Re-read of the column's activity log, for a host that shows one. Optional
   * because only the sheet does: a host with no log on screen has nothing that
   * could go stale, so demanding a refetch from it would buy a request no
   * reader is waiting on.
   */
  refetchActivityLogs?: () => Promise<void>;
}

/**
 * The surface `AnalyticsOverrideDialog` renders and the two hosts drive.
 *
 * Declared, not `ReturnType<typeof useAnalyticsOverrideFlow>`. The published
 * contract then changes only when this declaration is edited: an inferred
 * return type would enrol any state added inside the hook into the surface
 * silently, with no author having decided it should be public.
 *
 * The setters below are the raw state dispatchers, exposed because the dialog
 * writes the auditor's choices straight back through them. They let a host push
 * the flow into a state its own `close()` assumes only it produces, so a caller
 * that reaches past the dialog is trusted not to; narrowing them is a change to
 * this published contract and is left for its author to make.
 */
export interface AnalyticsOverrideFlow {
  isOpen: boolean;
  open: () => void;
  close: () => void;
  outcome: OutcomeType | undefined;
  setOutcome: Dispatch<SetStateAction<OutcomeType | undefined>>;
  conclusion: string;
  setConclusion: Dispatch<SetStateAction<string>>;
  populationTotal: string;
  setPopulationTotal: Dispatch<SetStateAction<string>>;
  populationPrompted: boolean;
  populationRequired: boolean;
  overrideSuperseded: boolean;
  canSubmit: boolean;
  isSubmitting: boolean;
  submit: () => Promise<void>;
}

/**
 * The `New Conclusion` override an analytics cell offers, as both surfaces that
 * offer it drive it: the sheet beside its activity log, and the work-paper
 * fold-out from its verdict pill.
 *
 * One flow rather than one per host. What the route demands of a conclusion —
 * an outcome and the auditor's own words together, a denominator where nothing
 * else can resolve one — and what it can answer with are properties of the
 * write, not of the surface that opened it; a second implementation would be
 * free to drift on the arm that matters least often and is hardest to reach.
 *
 * The dialog that collects the conclusion is `AnalyticsOverrideDialog`, which
 * renders whatever this returns.
 */
export function useAnalyticsOverrideFlow({
  auditSession,
  analyticsResultId,
  analyticsResult,
  column,
  checkResultId,
  sourceRowId,
  refetch,
  onRetarget,
  refetchActivityLogs,
}: UseAnalyticsOverrideFlowProps): AnalyticsOverrideFlow {
  const { invalidate } = useHookCache();
  const { isSubmitting, submitOverride } = useAnalyticsResultOverride();

  const [isOpen, setIsOpen] = useState(false);
  // Unchosen until the auditor chooses. A default would put an explicit "pass"
  // on the wire for an auditor who never picked one, and nothing downstream can
  // catch it: the outcome is required and "pass" is a valid answer, so the body
  // reads exactly like a deliberate one and would sign the judge's verdict as
  // the auditor's own.
  const [outcome, setOutcome] = useState<OutcomeType | undefined>(undefined);
  const [conclusion, setConclusion] = useState("");
  const [populationTotal, setPopulationTotal] = useState("");
  // Set when the route refuses this conclusion because the row it targets is no
  // longer the live one. The refusal is about the target, not the answer, so it
  // disarms Save while leaving the auditor's text on screen to carry over.
  const [overrideSuperseded, setOverrideSuperseded] = useState(false);

  // Tracks the live selection so an override that resolves after the auditor has
  // moved the host to another cell does not retarget onto the row they left.
  const selectedCheckResultId = useRef(checkResultId);
  selectedCheckResultId.current = checkResultId;

  // Closing the surface unmounts its host without cancelling a submit already in
  // flight, and the ref that submit captured survives holding its last selection
  // — so the id comparison alone still passes and would reopen the cell the
  // auditor just dismissed.
  const isMounted = useRef(true);
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  // An override is conclusive, so the route needs a denominator for it. It
  // resolves one from the verdict or, failing that, from the run manifest — and
  // refuses a supplied figure that contradicts either. The manifest id stands in
  // for "something else can answer" rather than the hydrated manifest's own
  // population, because the id is on the row itself while the manifest parses
  // `.catch(null)`: reading the population off the manifest would demand a
  // figure of every build that could not parse one, for a run the route can
  // still resolve. So the figure is asked for optionally. The stand-in is
  // inexact: a manifest whose own resolution refused carries no population, and
  // a blank answer on such a cell is refused with the route's own message.
  //
  // Both read a detail that may be absent, because the dialog outlives the
  // host's loading and error states: a cell whose own figures did not load
  // cannot say whether a denominator is needed, so none is asked for and none
  // is demanded. That case is delegated, not validated — with no detail
  // `canSubmit` can arm on outcome and conclusion alone, and the route is what
  // refuses a conclusion that needed a figure. It is left unguarded because a
  // detail that has loaded once stays loaded: `invalidate` overwrites
  // `entry.data` only on a successful re-read and leaves it standing on a
  // failed one.
  const populationPrompted = analyticsResult?.populationTotal === null;
  const populationRequired =
    populationPrompted && analyticsResult?.runManifestId == null;

  const parsedPopulation = Number(populationTotal);
  const populationValid =
    populationTotal.trim() !== "" &&
    Number.isInteger(parsedPopulation) &&
    parsedPopulation >= 0;

  // A required denominator must be a valid non-negative integer; an optional one
  // may also be left blank (the run's own figure answers) or never asked for.
  const populationAccepted = populationRequired
    ? populationValid
    : !populationPrompted || populationTotal.trim() === "" || populationValid;

  const canSubmit =
    outcome !== undefined &&
    conclusion.trim().length > 0 &&
    populationAccepted &&
    !overrideSuperseded;

  const open = useCallback(() => setIsOpen(true), []);

  // The outcome decides the recorded conclusion, so it clears with the text:
  // a dialog that blanks its text but keeps the outcome reads as fresh.
  const close = useCallback(() => {
    setIsOpen(false);
    setOutcome(undefined);
    setConclusion("");
    setPopulationTotal("");
    setOverrideSuperseded(false);
  }, []);

  const submit = async () => {
    if (!analyticsResultId) return;
    // Narrowing to the outcome the route requires, not a second gate:
    // `canSubmit` keeps Save disarmed until one is chosen.
    if (outcome === undefined) return;
    const submittedForCheckResultId = checkResultId;

    const override = await submitOverride(analyticsResultId, {
      outcome,
      outcomeDescription: conclusion,
      populationTotal: populationValid ? parsedPopulation : undefined,
    });

    // `undefined` is a refusal the auditor can answer where they are — a
    // contradicted figure, a cell with no denominator — so the dialog stays
    // open with their text and Save armed. The hook has already surfaced what
    // the route said to do about it.
    if (override === undefined) return;

    // `OVERRIDE_SUPERSEDED` is a refusal no retry outlives: the route answers
    // 409 once another write owns the live row, and this conclusion targets the
    // one it replaced. Re-reading this host's own detail is what the refusal
    // asks for and is what closes the override gate — the gate reads
    // `supersededAt`, and the cached one predates the winning write — and it is
    // expected back, since only the target's liveness changed. The shell is
    // re-read with it, because a 409 is proof this client's grid still holds the
    // row that lost the slot: left alone, closing the surface and re-opening the
    // cell lands on that same dead row and the notice's advice cannot be
    // followed. The log list is column-scoped, so the write that took the slot
    // belongs in it too, wherever a host is showing one. The dialog stays open
    // so the auditor's text is still on screen for them to copy into a new
    // conclusion — nothing carries it over — with `canSubmit` disarming Save so
    // it cannot be sent at the dead row again.
    //
    // The three re-reads are unordered against one another — the detail and
    // shell keys are disjoint, so neither invalidation reaches the other's
    // entry, and the log list keys off an id this refusal did not change — so
    // they run together. `Promise.all` and not `allSettled`: each absorbs its
    // own fetch failures, so nothing here is expected to reject, and a
    // rejection that escaped anyway should reach the caller rather than be
    // collected and dropped.
    if (override === OVERRIDE_SUPERSEDED) {
      setOverrideSuperseded(true);
      await Promise.all([
        refetch(),
        invalidate(["procedure-session", auditSession.id, "table-shell"]),
        refetchActivityLogs?.(),
      ]);
      return;
    }

    // Both saved outcomes close the dialog first. Retargeting swaps the detail
    // cache key on a host that never remounts between selections, so a dialog
    // left open here would re-appear prefilled with Save armed the moment the
    // successor's detail lands; on the unreadable path that armed Save is a 409
    // waiting to hit the row this write just superseded. Closing before the
    // retarget and the refetches is what shuts that window.
    close();

    let detailRetargeted = false;

    if (override) {
      toast.success("Conclusion saved");

      // The write may resolve after the auditor moved the host to another cell
      // or closed it altogether. Retargeting then would pull them back onto a
      // row they left, so only follow the successor while this host is still
      // open and still pointed at the row this submit was issued against.
      //
      // The column is read on the same terms: it resolves from the detail on
      // screen and names the procedure column the successor's selection points
      // at, so without it there is nothing to retarget onto.
      if (
        isMounted.current &&
        column &&
        selectedCheckResultId.current === submittedForCheckResultId
      ) {
        const next = analyticsSummaryToCheckResult(override, column);
        onRetarget({
          sourceRowId,
          checkResultId: next.id,
          evalColumnId: next.evalColumnId ?? undefined,
        });
        detailRetargeted = true;
      }
    }

    // `null` is committed-but-unreadable: the write landed but the successor
    // did not parse, so there is no object to retarget onto and no success
    // toast — the hook's own warning stands in for it. The refreshes below run
    // on both paths, because the supersession committed and a grid that is not
    // told would go on badging the now-superseded verdict.
    //
    // Only a retarget moves this host onto the successor's detail, so the
    // re-read is skipped wherever one happened: the array carries `undefined`
    // in its place, which `Promise.all` resolves without awaiting. Where the
    // host is still pointed at this row — no successor to point at, or no
    // column to retarget onto — the detail under this key is what the override
    // gate reads `supersededAt` from, and without the re-read that gate would
    // go on offering a conclusion against a row every write now refuses; that
    // is what the re-read is for. Where the host has unmounted or moved to
    // another cell the re-read serves nothing on screen — the shell re-read
    // beside it is what moves the grid onto the successor — and runs unguarded
    // anyway, at the price of a failed re-read toasting an auditor who has
    // already moved on, as that shell invalidation already can. Re-reading
    // this id is safe even where the successor failed to parse: what failed
    // was reading it back.
    await Promise.all([
      detailRetargeted ? undefined : refetch(),
      invalidate(["procedure-session", auditSession.id, "table-shell"]),
      refetchActivityLogs?.(),
    ]);
  };

  return {
    isOpen,
    open,
    close,
    outcome,
    setOutcome,
    conclusion,
    setConclusion,
    populationTotal,
    setPopulationTotal,
    populationPrompted,
    populationRequired,
    overrideSuperseded,
    canSubmit,
    isSubmitting,
    submit,
  };
}
